import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout
} from 'react-resizable-panels';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import useOutletContext from '../useOutletContext';
import type { ContextType } from '../useOutletContext';

import '@/lib/pdfWorker';
import ReportsFieldsPanel from './components/ReportsFieldsPanel';
import ReportsHeader from './components/ReportsHeader';
import ReportsPdfPreview from './components/ReportsPdfPreview';
import ReportsSlidesPanel from './components/ReportsSlidesPanel';
import { type Json, type SduiItem } from './components/SduiItemsRenderer';
import { type OutlineEntry, REPORT_TABS, type ReportKey } from './types';
import {
  decodeBase64ToPdfFile,
  findOutlineTitleForPage,
  loadOutlineEntries
} from './utils';

import { Button } from '@/components/ui/button';

const SDUI_PAGE_PAYLOAD = gql`
  query SduiPagePayload($opportunityId: ID!, $pageKey: String!) {
    sduiPagePayload(opportunityId: $opportunityId, pageKey: $pageKey) {
      items
    }
  }
`;

const GENERATE_DIS_REPORT = gql`
  mutation GenerateDisReport($id: ID!) {
    generateDisReport(opportunityId: $id) {
      data
      message
      ok
    }
  }
`;

const GENERATE_RSM_REPORT = gql`
  mutation GenerateRSM($id: ID!) {
    generateRsmReport(opportunityId: $id) {
      data
      message
      ok
    }
  }
`;

const GENERATE_ICB_REPORT = gql`
  mutation GenerateIcbReport($id: ID!) {
    generateIcbReport(opportunityId: $id) {
      data
      message
      ok
    }
  }
`;

const GENERATE_ICM_REPORT = gql`
  mutation GenerateICMReport($id: ID!) {
    generateIcmReport(opportunityId: $id) {
      data
      message
      ok
    }
  }
`;

const GENERATE_FICM_REPORT = gql`
  mutation GenerateFICMReport($id: ID!) {
    generateFicmReport(opportunityId: $id) {
      data
      message
      ok
    }
  }
`;

const REPORTS_LAYOUT_ID = 'investmentsDirectReportsLayout';
const REPORTS_FIELDS_PANEL_ID = 'investmentsDirectReportsFieldsPanel';
const REPORTS_PREVIEW_PANEL_ID = 'investmentsDirectReportsPreviewPanel';

const Reports = () => {
  const location = useLocation();
  const locationState = (location.state ?? {}) as {
    initialReportKey?: ReportKey;
    opportunityId?: string;
    projectName?: string;
  };
  const outletContext = useOutletContext() as Partial<ContextType> | undefined;
  const opportunityId = outletContext?.id ?? locationState.opportunityId;
  const projectName = outletContext?.projectName ?? locationState.projectName;
  const initialReportKey =
    locationState.initialReportKey &&
    REPORT_TABS.some(tab => tab.key === locationState.initialReportKey)
      ? locationState.initialReportKey
      : 'icb';

  const [selectedReport, setSelectedReport] =
    useState<ReportKey>(initialReportKey);
  const [fileData, setFileData] = useState<null | string>(null);
  const [page, setPage] = useState<number>(1);
  const [isFinalPreview, setIsFinalPreview] = useState(false);
  const [numPages, setNumPages] = useState<null | number>(null);
  const [loadError, setLoadError] = useState<null | string>(null);
  const [outlineEntries, setOutlineEntries] = useState<OutlineEntry[]>([]);
  const [isRefetchingReport, setIsRefetchingReport] = useState(false);
  const [pdfRenderKey, setPdfRenderKey] = useState(0);
  const editedFieldValuesRef = useRef<Record<string, Json>>({});
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: REPORTS_LAYOUT_ID,
    panelIds: [REPORTS_FIELDS_PANEL_ID, REPORTS_PREVIEW_PANEL_ID]
  });

  const [generateDisReport] = useMutation<{
    generateDisReport?: { data?: null | string };
  }>(GENERATE_DIS_REPORT);
  const [generateRsmReport] = useMutation<{
    generateRsmReport?: { data?: null | string };
  }>(GENERATE_RSM_REPORT);
  const [generateIcbReport] = useMutation<{
    generateIcbReport?: { data?: null | string };
  }>(GENERATE_ICB_REPORT);
  const [generateIcmReport] = useMutation<{
    generateIcmReport?: { data?: null | string };
  }>(GENERATE_ICM_REPORT);
  const [generateFicmReport] = useMutation<{
    generateFicmReport?: { data?: null | string };
  }>(GENERATE_FICM_REPORT);

  const titleForCurrentPage = useMemo(() => {
    const fromOutline = findOutlineTitleForPage(outlineEntries, page);
    return ((fromOutline ?? `Page ${String(page)}`) || '').trim();
  }, [outlineEntries, page]);
  const currentSectionRange = useMemo(() => {
    if (!outlineEntries.length || !numPages) return null;

    let sectionIndex = -1;
    for (let i = 0; i < outlineEntries.length; i += 1) {
      if ((outlineEntries[i]?.pageNumber ?? 0) <= page) {
        sectionIndex = i;
      } else {
        break;
      }
    }
    if (sectionIndex < 0) return null;

    const startPage = outlineEntries[sectionIndex]?.pageNumber ?? page;
    const endPage =
      (outlineEntries[sectionIndex + 1]?.pageNumber ?? numPages + 1) - 1;
    return {
      endPage,
      indexInSection: page - startPage + 1,
      pagesInSection: endPage - startPage + 1,
      startPage
    };
  }, [numPages, outlineEntries, page]);
  const currentNavRange = useMemo(() => {
    if (!numPages) return null;
    if (isFinalPreview) {
      return {
        endPage: numPages,
        indexInSection: page,
        pagesInSection: numPages,
        startPage: 1
      };
    }
    return currentSectionRange;
  }, [currentSectionRange, isFinalPreview, numPages, page]);

  const {
    data: sduiData,
    error: sduiError,
    loading: sduiLoading
  } = useQuery<{
    sduiPagePayload?: { items?: unknown };
  }>(SDUI_PAGE_PAYLOAD, {
    skip: !opportunityId,
    variables: {
      opportunityId,
      pageKey: titleForCurrentPage
    }
  });

  const sduiItems = useMemo(() => {
    const raw = sduiData?.sduiPagePayload?.items;
    if (!Array.isArray(raw)) return [];
    return raw as SduiItem[];
  }, [sduiData]);

  const handleSduiFieldChange = useCallback((path: string, value: Json) => {
    editedFieldValuesRef.current[path] = value;
  }, []);

  const decoded = useMemo(() => {
    if (!fileData) {
      return {
        error: null as null | string,
        file: null as { data: Uint8Array } | null
      };
    }

    return decodeBase64ToPdfFile(fileData);
  }, [fileData]);
  const documentFile = useMemo(() => {
    if (!decoded.file) return null;
    const renderCycle = pdfRenderKey;
    void renderCycle;
    const previewMode = isFinalPreview;
    void previewMode;
    return { data: decoded.file.data.slice() };
  }, [decoded.file, isFinalPreview, pdfRenderKey]);

  const pdfError = decoded.error ?? loadError;

  const fetchSelectedReport = useCallback(
    async (reportKey: ReportKey) => {
      if (!opportunityId) {
        toast.error(
          'Missing opportunity context. Please open this page from a deal.'
        );
        return;
      }

      setIsRefetchingReport(true);
      setLoadError(null);

      try {
        let base64File = '';

        if (reportKey === 'dis') {
          const { data, error } = await generateDisReport({
            variables: { id: opportunityId }
          });
          if (error) throw new Error(error.message);
          base64File = data?.generateDisReport?.data ?? '';
        } else if (reportKey === 'rsm') {
          const { data, error } = await generateRsmReport({
            variables: { id: opportunityId }
          });
          if (error) throw new Error(error.message);
          base64File = data?.generateRsmReport?.data ?? '';
        } else if (reportKey === 'icb') {
          const { data, error } = await generateIcbReport({
            variables: { id: opportunityId }
          });
          if (error) throw new Error(error.message);
          base64File = data?.generateIcbReport?.data ?? '';
        } else if (reportKey === 'icm') {
          const { data, error } = await generateIcmReport({
            variables: { id: opportunityId }
          });
          if (error) throw new Error(error.message);
          base64File = data?.generateIcmReport?.data ?? '';
        } else {
          const { data, error } = await generateFicmReport({
            variables: { id: opportunityId }
          });
          if (error) throw new Error(error.message);
          base64File = data?.generateFicmReport?.data ?? '';
        }

        if (!base64File.trim()) {
          setFileData(null);
          toast.error('Report is not available.');
          return;
        }

        setFileData(base64File);
        setPdfRenderKey(k => k + 1);
      } catch (error_) {
        const error = error_ as Error;
        console.error(error);
        toast.error('Failed to generate report');
      } finally {
        setIsRefetchingReport(false);
      }
    },
    [
      generateDisReport,
      generateFicmReport,
      generateIcbReport,
      generateIcmReport,
      generateRsmReport,
      opportunityId
    ]
  );

  useEffect(() => {
    void fetchSelectedReport(selectedReport);
  }, [fetchSelectedReport, selectedReport]);

  const toggleReport = (reportKey: ReportKey) => {
    setSelectedReport(reportKey);
    setIsFinalPreview(false);
    setNumPages(null);
    setOutlineEntries([]);
    setPage(1);
  };

  return (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <ReportsHeader
        isRefetchingReport={isRefetchingReport}
        onRefresh={() => {
          void fetchSelectedReport(selectedReport);
        }}
        onSelectReport={toggleReport}
        opportunityId={opportunityId}
        projectName={projectName}
        selectedReport={selectedReport}
        titleForCurrentPage={titleForCurrentPage}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <ReportsSlidesPanel
          entries={outlineEntries}
          isFinalPreview={isFinalPreview}
          onSelectFinalPreview={() => {
            setIsFinalPreview(true);
          }}
          onSelectPage={pageNumber => {
            setIsFinalPreview(false);
            setPage(pageNumber);
          }}
          page={page}
        />

        {isFinalPreview ? (
          <div className="flex min-w-0 flex-1 flex-col">
            {currentNavRange && currentNavRange.pagesInSection > 1 ? (
              <div className="flex items-center justify-end gap-2 px-4 pt-2">
                <Button
                  disabled={page <= currentNavRange.startPage}
                  onClick={() => {
                    setPage(p => Math.max(currentNavRange.startPage, p - 1));
                  }}
                  size="icon"
                  variant="outline"
                >
                  <ChevronLeftIcon />
                </Button>
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {String(currentNavRange.indexInSection)} /{' '}
                  {String(currentNavRange.pagesInSection)}
                </span>
                <Button
                  disabled={page >= currentNavRange.endPage}
                  onClick={() => {
                    setPage(p => Math.min(currentNavRange.endPage, p + 1));
                  }}
                  size="icon"
                  variant="outline"
                >
                  <ChevronRightIcon />
                </Button>
              </div>
            ) : null}
            <ReportsPdfPreview
              file={documentFile}
              numPages={numPages}
              onLoadError={error => {
                console.error('Failed to load report PDF:', error);
                setLoadError('Failed to load report preview');
                toast.error('Failed to load report preview');
              }}
              onLoadSuccess={pdf => {
                setNumPages(pdf.numPages);
                setPage(p => Math.min(pdf.numPages, p));
                setLoadError(null);
                loadOutlineEntries(pdf)
                  .then(({ entries }) => {
                    setOutlineEntries(entries);
                  })
                  .catch(() => {
                    setOutlineEntries([]);
                  });
              }}
              onSourceError={error => {
                console.error('Failed to read report PDF source:', error);
                setLoadError('Failed to read report preview source');
                toast.error('Failed to load report preview');
              }}
              opportunityId={opportunityId}
              page={page}
              pdfError={pdfError}
              pdfRenderKey={pdfRenderKey}
            />
          </div>
        ) : (
          <Group
            className="min-w-0 flex-1"
            defaultLayout={defaultLayout}
            onLayoutChanged={onLayoutChanged}
          >
            <Panel
              id={REPORTS_FIELDS_PANEL_ID}
              minSize={320}
            >
              <ReportsFieldsPanel
                currentPageTitle={titleForCurrentPage}
                errorMessage={sduiError?.message}
                isLoading={sduiLoading}
                items={sduiItems}
                onChange={handleSduiFieldChange}
              />
            </Panel>
            <Separator className="group relative w-1.5 bg-transparent transition-colors hover:bg-blue-500/10 active:bg-blue-500/20">
              <div className="bg-border absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors group-hover:bg-blue-500 group-active:bg-blue-600" />
            </Separator>
            <Panel
              id={REPORTS_PREVIEW_PANEL_ID}
              minSize={480}
            >
              <div className="flex h-full min-h-0 flex-col">
                {currentNavRange && currentNavRange.pagesInSection > 1 ? (
                  <div className="flex items-center justify-end gap-2 px-4 pt-2">
                    <Button
                      disabled={page <= currentNavRange.startPage}
                      onClick={() => {
                        setPage(p => Math.max(currentNavRange.startPage, p - 1));
                      }}
                      size="icon"
                      variant="outline"
                    >
                      <ChevronLeftIcon />
                    </Button>
                    <span className="text-muted-foreground text-xs whitespace-nowrap">
                      {String(currentNavRange.indexInSection)} /{' '}
                      {String(currentNavRange.pagesInSection)}
                    </span>
                    <Button
                      disabled={page >= currentNavRange.endPage}
                      onClick={() => {
                        setPage(p => Math.min(currentNavRange.endPage, p + 1));
                      }}
                      size="icon"
                      variant="outline"
                    >
                      <ChevronRightIcon />
                    </Button>
                  </div>
                ) : null}
                <ReportsPdfPreview
                  file={documentFile}
                  numPages={numPages}
                  onLoadError={error => {
                    console.error('Failed to load report PDF:', error);
                    setLoadError('Failed to load report preview');
                    toast.error('Failed to load report preview');
                  }}
                  onLoadSuccess={pdf => {
                    setNumPages(pdf.numPages);
                    setPage(p => Math.min(pdf.numPages, p));
                    setLoadError(null);
                    loadOutlineEntries(pdf)
                      .then(({ entries }) => {
                        setOutlineEntries(entries);
                      })
                      .catch(() => {
                        setOutlineEntries([]);
                      });
                  }}
                  onSourceError={error => {
                    console.error('Failed to read report PDF source:', error);
                    setLoadError('Failed to read report preview source');
                    toast.error('Failed to load report preview');
                  }}
                  opportunityId={opportunityId}
                  page={page}
                  pdfError={pdfError}
                  pdfRenderKey={pdfRenderKey}
                />
              </div>
            </Panel>
          </Group>
        )}
      </div>
    </div>
  );
};

export default Reports;
